/**
 * Provider de OMDB para transacciones
 * Usa la API del explorer de OMDB
 */

import { Transaction, ExplorerTransaction } from '@/types/transaction.types';
import { BaseTransactionProvider } from './base-provider';
import { getApiBaseUrl } from '@/config/app';

export class OMDBProvider extends BaseTransactionProvider {
  private apiUrl: string;

  constructor(apiUrl: string, _rpcUrl: string, timeout = 5000) {
    super('omdb', timeout);
    this.apiUrl = apiUrl;
  }


  async getTransactions(address: string, fromBlock?: number): Promise<Transaction[]> {
    try {
      console.log(`[OMDB] Fetching transactions for ${address}`);

      const [normalTxs, internalTxs] = await Promise.all([
        this.getNormalTransactions(address, fromBlock),
        this.getInternalTransactions(address, fromBlock),
      ]);

      const allTransactions = [...normalTxs, ...internalTxs];
      
      // Eliminar duplicados por hash
      const uniqueTransactions = allTransactions.filter((tx, index, self) => 
        index === self.findIndex(t => t.hash === tx.hash)
      );

      // Ordenar por timestamp descendente
      uniqueTransactions.sort((a, b) => b.timestamp - a.timestamp);

      console.log(`[OMDB] Found ${uniqueTransactions.length} transactions`);
      return uniqueTransactions;
    } catch (error) {
      console.error('[OMDB] Error fetching transactions:', error);
      await this.handleRateLimit(error as Error);
      return [];
    }
  }

  async getTokenTransfers(address: string, contractAddress?: string): Promise<Transaction[]> {
    try {
      console.log(`[OMDB] Fetching token transfers for ${address}`);

      const url = new URL(`${this.apiUrl}/api/`);
      url.searchParams.set('module', 'account');
      url.searchParams.set('action', 'tokentx');
      url.searchParams.set('address', address);
      url.searchParams.set('sort', 'desc');
      
      if (contractAddress) {
        url.searchParams.set('contractaddress', contractAddress);
      }

      const response = await this.fetchWithTimeout(url.toString());
      const data = await response.json();

      if (data.status !== '1') {
        // Cualquier variante de "no hay resultados" es un caso normal, no un error
        return [];
      }

      const transactions = data.result.map((tx: any) => this.convertExplorerTransaction(tx, address, true));

      console.log(`[OMDB] Found ${transactions.length} token transfers`);
      return transactions;
    } catch (error) {
      console.warn('[OMDB] Error fetching token transfers:', error);
      await this.handleRateLimit(error as Error);
      return [];
    }
  }

  async getTransactionDetails(txHash: string): Promise<Transaction | null> {
    // Camino B: consultar el servidor primero (protege el acceso al explorer OMDB)
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/transfers/status/${txHash}?network=omdb`, {
        signal: AbortSignal.timeout(10_000),
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          status: 'pending' | 'confirmed' | 'failed';
          blockNumber?: string;
        };

        if (data.success) {
          if (data.status === 'pending') return null;

          return {
            hash: txHash,
            from: '',
            to: '',
            value: '0',
            timestamp: 0,
            blockNumber: data.blockNumber ? parseInt(data.blockNumber) : 0,
            status: data.status,
            type: 'send',
            network: this.network,
            source: 'server_sync',
          };
        }
      }
    } catch {
      // Servidor no disponible — usar explorer OMDB directamente
    }

    // Fallback: consultar el explorer OMDB directamente
    console.log(`[OMDB] Servidor no disponible, usando explorer directo`);
    return this.getTransactionDetailsFromExplorer(txHash);
  }

  /** Lógica original contra el explorer OMDB (ahora es el fallback) */
  private async getTransactionDetailsFromExplorer(txHash: string): Promise<Transaction | null> {
    try {
      // 1. Verificar el estado del recibo (confirmed/failed)
      const receiptUrl = new URL(`${this.apiUrl}/api/`);
      receiptUrl.searchParams.set('module', 'transaction');
      receiptUrl.searchParams.set('action', 'gettxreceiptstatus');
      receiptUrl.searchParams.set('txhash', txHash);

      const receiptResponse = await this.fetchWithTimeout(receiptUrl.toString());
      const receiptData = await receiptResponse.json();

      // Si el recibo no existe aún, la tx sigue pendiente
      if (receiptData.status !== '1') {
        return null;
      }

      const txStatus: 'confirmed' | 'failed' =
        receiptData.result?.status === '1' ? 'confirmed' : 'failed';

      // 2. Obtener detalles de la transacción para el blockNumber
      const detailsUrl = new URL(`${this.apiUrl}/api/`);
      detailsUrl.searchParams.set('module', 'proxy');
      detailsUrl.searchParams.set('action', 'eth_getTransactionByHash');
      detailsUrl.searchParams.set('txhash', txHash);

      const detailsResponse = await this.fetchWithTimeout(detailsUrl.toString());
      const detailsData = await detailsResponse.json();

      if (!detailsData.result) {
        return null;
      }

      const tx = detailsData.result;
      const blockNumber = tx.blockNumber ? parseInt(tx.blockNumber, 16) : 0;

      return {
        hash: tx.hash,
        from: tx.from,
        to: tx.to || '',
        value: this.parseValue(tx.value ?? '0x0'),
        timestamp: 0, // Será ignorado por updateTransactionStatus
        blockNumber,
        status: txStatus,
        type: 'send',   // Será ignorado por updateTransactionStatus
        network: this.network,
        gasUsed: tx.gas ? parseInt(tx.gas, 16).toString() : undefined,
        gasPrice: tx.gasPrice ? parseInt(tx.gasPrice, 16).toString() : undefined,
        source: 'explorer_sync',
      };
    } catch (error) {
      console.error('[OMDB] Error fetching transaction details:', error);
      return null;
    }
  }

  private async getNormalTransactions(address: string, fromBlock?: number): Promise<Transaction[]> {
    return this.getNormalTransactionsFromExplorer(address, fromBlock);
  }

  private async getNormalTransactionsFromExplorer(address: string, fromBlock?: number): Promise<Transaction[]> {
    const url = new URL(`${this.apiUrl}/api/`);
    url.searchParams.set('module', 'account');
    url.searchParams.set('action', 'txlist');
    url.searchParams.set('address', address);
    url.searchParams.set('sort', 'desc');
    
    if (fromBlock) {
      url.searchParams.set('startblock', fromBlock.toString());
    }

    const response = await this.fetchWithTimeout(url.toString());
    const data = await response.json();

    if (data.status !== '1') {
      if (data.message === 'No transactions found') {
        return [];
      }
      return [];
    }

    return data.result.map((tx: ExplorerTransaction) => 
      this.convertExplorerTransaction(tx, address, false)
    );
  }


  private async getInternalTransactions(address: string, fromBlock?: number): Promise<Transaction[]> {
    try {
      const url = new URL(`${this.apiUrl}/api/`);
      url.searchParams.set('module', 'account');
      url.searchParams.set('action', 'txlistinternal');
      url.searchParams.set('address', address);
      url.searchParams.set('sort', 'desc');
      
      if (fromBlock) {
        url.searchParams.set('startblock', fromBlock.toString());
      }

      const response = await this.fetchWithTimeout(url.toString());
      const data = await response.json();

      if (data.status !== '1') {
        if (data.message === 'No transactions found') {
          return [];
        }
        // No lanzar error para transacciones internas, solo log
        console.warn('[OMDB] No internal transactions found');
        return [];
      }

      return data.result.map((tx: ExplorerTransaction) => 
        this.convertExplorerTransaction(tx, address, false)
      );
    } catch (error) {
      console.warn('[OMDB] Error fetching internal transactions:', error);
      return []; // No fallar por transacciones internas
    }
  }

  private convertExplorerTransaction(tx: ExplorerTransaction, userAddress: string, isToken: boolean): Transaction {
    return {
      hash: tx.hash,
      from: tx.from,
      to: tx.to,
      value: this.parseValue(tx.value, isToken ? undefined : 18),
      timestamp: this.parseTimestamp(tx.timeStamp),
      blockNumber: parseInt(tx.blockNumber),
      status: tx.isError === '0' ? 'confirmed' : 'failed',
      type: this.getTransactionType(tx.from, tx.to, userAddress),
      network: this.network,
      gasUsed: tx.gasUsed,
      gasPrice: tx.gasPrice,
      source: 'explorer_sync',
    };
  }
}